import React, { Fragment, useRef } from 'react';
import { useFormState } from 'react-final-form';
import { isAfter } from 'date-fns';
import styled from 'styled-components';

import ErrorMessage from '@common/fields/_common/errorMessage';
import { Heading4 } from '@common/Typography';
import { useAutoscroll } from '@common/utils/hooks/useAutoScroll';
import { useScopedTranslation } from '@common/utils/hooks/useScopedTranslation';
import { isDifferentTimezone } from '@utils/date';

import Section from '../_common/Section';
import SectionHeader from '../_common/SectionHeader';

import { useTimeTableState } from './hooks/useTimeTablesState';
import Header from './Header';
import TimeTable from './TimeTable';

const Error = styled(ErrorMessage)`
  height: 2rem;
  margin-bottom: 1rem;
  margin-top: 0.2rem;
  text-align: center;
  width: 100%;
`;

const TimezoneWarning = styled.div`
  color: ${({ theme }) => theme.colors.GREY_3};
  font-size: 0.75rem;
  margin-left: auto;
`;

const OfficeTitle = styled(Heading4)`
  align-self: center;
`;

export default function TimeTables() {
  const { t } = useScopedTranslation();
  const timeTableRef = useRef<HTMLDivElement>({} as HTMLDivElement);

  const {
    values: { meetingType },
  } = useFormState();

  const {
    end,
    handleCancelClick,
    handleNextClick,
    handlePreviousClick,
    hasReachedEndOfRange,
    isSearching,
    officeGroups,
    start,
    timetableError,
    timezone,
  } = useTimeTableState();

  const sortedOfficeGroups = [...officeGroups].sort(
    (officeGroupA, officeGroupB) =>
      officeGroupA.office.title.localeCompare(officeGroupB.office.title),
  );

  useAutoscroll(timeTableRef);

  return (
    <Section ref={timeTableRef}>
      <SectionHeader>
        {t(`main_form.timetable.header.${meetingType.toLowerCase()}`)}
      </SectionHeader>
      <Header
        disableNext={hasReachedEndOfRange}
        disablePrevious={isAfter(new Date(), start)}
        endDate={end}
        isSearching={isSearching}
        onCancelClick={handleCancelClick}
        onNextClick={handleNextClick}
        onPreviousClick={handlePreviousClick}
        startDate={start}
        timezone={timezone}
      />
      {timetableError && <Error>{t('errors.technical')}</Error>}
      {sortedOfficeGroups.map(({ groups, office }) => (
        <Fragment key={office.id}>
          <div className="flex items-center justify-center pb-2 pt-4">
            <OfficeTitle>{office.title}</OfficeTitle>
            {isDifferentTimezone(timezone) && (
              <TimezoneWarning>
                {t('main_form.timetable.header.timezone_warning')}
              </TimezoneWarning>
            )}
          </div>
          <TimeTable
            end={end}
            groups={groups}
            isSearching={isSearching}
            office={office}
            start={start}
            timezone={timezone!}
          />
        </Fragment>
      ))}
    </Section>
  );
}
